import { getFnolRequiredClaimAttributes } from '@dispatch/shared/services';
import { useNotification } from '@dispatch/web/hooks';
import { isEmpty } from 'lodash';
import { useQuery as useReactQuery } from 'react-query';

const FIVE_MINUTES = 5 * 60 * 1000;

const NO_ATTRIBUTE_NAMES: string[] = [];

export type FnolRequiredClaimAttributesResult = {
  attributeNames: string[];
  isLoading: boolean;
};

/**
 * useFnolRequiredClaimAttributes - the tenant's configured list of claim
 * attribute display names the qualified FNOL flow must collect.
 *
 * Disabled (no request, not loading, no names) until a tenant is known. A
 * failed read is retried once, then fails open to no names and notifies.
 */
export const useFnolRequiredClaimAttributes = (
  tenantId: string | undefined
): FnolRequiredClaimAttributesResult => {
  const { notifyError } = useNotification();
  const { data, isLoading } = useReactQuery(
    ['fnol-required-claim-attributes', tenantId],
    () => getFnolRequiredClaimAttributes(tenantId ?? ''),
    {
      enabled: !isEmpty(tenantId),
      staleTime: FIVE_MINUTES,
      retry: 1,
      // A late or failed refetch on focus must not re-lock or re-seed a form
      // the user is already filling in.
      refetchOnWindowFocus: false,
      onError: () => {
        notifyError(
          'Required claim attributes unavailable',
          'Claim attributes are not required for this referral. Please try again later.'
        );
      },
    }
  );

  return {
    attributeNames: data?.attributeNames ?? NO_ATTRIBUTE_NAMES,
    isLoading,
  };
};
